import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from './context/FormContext';
import { AppConfigProvider, useAppConfig } from './context/AppConfigContext';
import { FUNNEL_ORDER } from './funnel/FUNNEL_ORDER';
import { KCAL_FUNNEL_ORDER } from './funnel/KCAL_FUNNEL_ORDER';
import { resolveFunnelMode } from './funnel/funnelMode';
import { FunnelModeContext } from './funnel/FunnelModeContext';
import { StepGatoComingSoon } from './steps/StepGato_ComingSoon';
import { AnimalType } from './types';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ChatWidget } from './components/funnel/ChatWidget';
import { ResumeModal } from './components/funnel/ResumeModal';
import { documentTitleForStep } from './utils/funnelSteps';
import { useAnalytics } from './hooks/useAnalytics';
import { loadDraft } from './utils/localStorageDraft';
import { shouldShowResumeModal } from './utils/shouldShowResumeModal';
import { CountryGatePage } from './pages/CountryGatePage';
import { MaintenanceScreen } from './components/operations/MaintenanceScreen';
import { PrestaShopGateScreen } from './components/operations/PrestaShopGateScreen';
import { ConfigLoadingScreen, ConfigUnavailableScreen } from './components/operations/ConfigUnavailableScreen';
import { getCountry, GeoCountryResponse } from './services/geo';
import { useUpdateMode } from './hooks/useUpdateMode';
import { getPetData } from './services/customerAccount';

const AdminConfiguracion = lazy(() =>
  import('./pages/AdminConfiguracion').then((m) => ({ default: m.AdminConfiguracion })),
);

const MiCuentaPage = lazy(() => import('./pages/MiCuentaPage').then((m) => ({ default: m.MiCuentaPage })));

const GEO_SESSION_KEY = 'myl_geo';

const GEO_DEBUG = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug_geo');

function GeoDebugBadge({ geoResult }: { geoResult: GeoCountryResponse | null }) {
  if (!GEO_DEBUG) return null;
  const pending = geoResult === null;
  const country = geoResult?.country ?? '—';
  const supported = geoResult?.is_supported;
  const color = pending ? '#666' : supported ? '#16a34a' : '#dc2626';
  const label = pending ? '⏳ geo: detectando…' : `🌍 ${country} · ${supported ? '✅ soportado' : '🚫 no soportado'}`;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 9999,
        background: '#1a1a1a',
        color: '#fff',
        borderRadius: 8,
        padding: '6px 12px',
        fontSize: 12,
        fontFamily: 'monospace',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,.4)',
        maxWidth: 260,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 2 }}>debug_geo</div>
      <div>{label}</div>
      {geoResult && (
        <div style={{ color: '#aaa', marginTop: 2 }}>soportados: {geoResult.supported_countries.join(', ')}</div>
      )}
    </div>
  );
}

function useGeoGate(): {
  geoResult: GeoCountryResponse | null;
  setGeoResult: React.Dispatch<React.SetStateAction<GeoCountryResponse | null>>;
} {
  const [geoResult, setGeoResult] = useState<GeoCountryResponse | null>(() => {
    try {
      const cached = sessionStorage.getItem(GEO_SESSION_KEY);
      if (cached) return JSON.parse(cached) as GeoCountryResponse;
    } catch {
      // ignore corrupt cache
    }
    return null;
  });

  const fetched = useRef(geoResult !== null);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    // Skip the network call in unit tests: no MSW mock exists for /geo/country
    // and a hanging fetch keeps Jest from exiting cleanly.
    if (process.env.NODE_ENV === 'test') return;

    // No abort/cancellation: detection is idempotent and StrictMode's immediate
    // cleanup would otherwise discard the result before setGeoResult fires.
    (async () => {
      try {
        const urlOverride = new URLSearchParams(window.location.search).get('country');
        const result = await getCountry(urlOverride);
        try {
          sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify(result));
        } catch {
          /* noop */
        }
        setGeoResult(result);
      } catch {
        // Network error — fail-open, funnel renders normally
      }
    })();
  }, []);

  return { geoResult, setGeoResult };
}

function UpdateModeLoader({ name }: { name: string }) {
  return (
    <div className="min-h-screen bg-[#F7F4EE] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-sm">
        <div className="w-10 h-10 border-4 border-[#008BBC] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-fredoka text-lg text-[#1a1a1a]">Cargando datos de {name}...</p>
      </div>
    </div>
  );
}

function UpdateModeError({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-[#F7F4EE] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-sm space-y-4">
        <span className="text-4xl">⚠️</span>
        <p className="font-fredoka text-lg text-[#1a1a1a]">No pudimos cargar los datos</p>
        <p className="font-poppins text-sm text-black/60">{message}</p>
        <a
          href="/mi-cuenta"
          className="block w-full py-3 rounded-xl font-fredoka text-base font-medium bg-[#008BBC] text-white hover:bg-[#007aa8] transition-colors"
        >
          Volver a Mi Cuenta
        </a>
      </div>
    </div>
  );
}

function AppContent() {
  const { currentStep, formData, resetFunnel, updateFormData, resetForNewPet } = useForm();
  const { config, configStatus, isLoading, refreshConfig, platform } = useAppConfig();
  const { geoResult, setGeoResult } = useGeoGate();

  const funnelMode = resolveFunnelMode(
    typeof window !== 'undefined' ? window.location.pathname : '/',
    config.runtime_controls?.full_funnel_enabled,
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const order = funnelMode === 'kcal' ? KCAL_FUNNEL_ORDER : FUNNEL_ORDER;

  const handleCountryOverride = useCallback(
    (countryCode: string) => {
      if (!geoResult) return;
      const updated: GeoCountryResponse = {
        ...geoResult,
        country: countryCode,
        is_supported: true,
      };
      try {
        sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify(updated));
      } catch {
        /* noop */
      }
      setGeoResult(updated);
    },
    [geoResult, setGeoResult],
  );
  const updateMode = useUpdateMode();

  const hydrationAttempted = useRef(false);
  const [updateHydrating, setUpdateHydrating] = useState(updateMode.isUpdateMode);
  const [updateHydrationError, setUpdateHydrationError] = useState('');

  // Hidratación del formulario en update mode: carga los datos del perro
  // desde el backend y los vuelca en el FormContext. Solo se ejecuta una vez
  // al montar (cuando isUpdateMode es true y hay petId disponible).
  useEffect(() => {
    if (!updateMode.isUpdateMode || !updateMode.petId || hydrationAttempted.current) return;
    hydrationAttempted.current = true;

    (async () => {
      try {
        resetForNewPet();
        const petData = await getPetData(updateMode.petId!);
        updateFormData(petData);
      } catch (err) {
        setUpdateHydrationError((err as Error).message || 'No pudimos cargar los datos del perro.');
      } finally {
        setUpdateHydrating(false);
      }
    })();
    // Solo al montar — las dependencias de updateMode son estables (session storage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyticsDogProfile = useMemo(() => {
    const breedKey = formData.breed_code || formData.raza;
    const breedEntry = breedKey ? config.breed_database?.breeds?.[breedKey] : undefined;
    return {
      ageCategory: formData.ageCategory,
      breedSize: breedEntry?.breed_size,
      pathologies: formData.pathologies,
      peso: formData.peso,
      animalType: formData.animalType,
    };
  }, [
    config.breed_database?.breeds,
    formData.ageCategory,
    formData.animalType,
    formData.breed_code,
    formData.pathologies,
    formData.peso,
    formData.raza,
  ]);
  const { trackStepChange } = useAnalytics(analyticsDogProfile);

  const [showResumeModal] = useState(() => {
    const draft = loadDraft();
    return shouldShowResumeModal(
      draft,
      typeof window !== 'undefined' ? window.location.search : '',
      typeof window !== 'undefined' ? window.location.pathname : '/',
    );
  });
  const [modalDismissed, setModalDismissed] = useState(false);
  const [draftSnapshot] = useState(() => loadDraft());

  useEffect(() => {
    trackStepChange(currentStep);
  }, [currentStep, trackStepChange]);

  // Document title dinamico por step. LiveChat (y cualquier analytics)
  // captura esto como "visited page" con titulo distinto por step, aunque
  // la URL sea la misma SPA.
  useEffect(() => {
    document.title = documentTitleForStep(currentStep);
  }, [currentStep]);

  // Scroll-to-top en cada cambio de step. iOS Safari needs both window.scrollTo
  // and direct scrollTop assignment to reliably scroll back to top. Step9_v2..
  // Step13 usan body scroll; FunnelLayout P1-P9 marca el contenedor interno
  // con data-funnel-scroll (overflow-y-auto).
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.querySelectorAll<HTMLElement>('[data-funnel-scroll]').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [currentStep]);

  // Gate de config remota — va ANTES que todo (incl. maintenance, que solo
  // tiene sentido con config real). Sin config del backend NUNCA se muestran
  // datos transaccionales (precios, recetas, razas). Excepción deliberada:
  // P1 (welcome) solo usa textos de marca con fallback, así que se renderiza
  // al instante mientras app-config carga (en 3G el fetch tarda 1-2s y
  // bloquear el primer paint dañaba LCP/conversión en la pantalla de mayor
  // tráfico); el gate se aplica al avanzar a P2+. Admin y /mi-cuenta no
  // pasan por aquí.
  if (configStatus === 'loading' && currentStep > 1) {
    return <ConfigLoadingScreen />;
  }
  if (configStatus === 'unavailable' && currentStep > 1) {
    return <ConfigUnavailableScreen onRetry={() => refreshConfig()} retrying={isLoading} />;
  }

  // Maintenance kill switch (ops) — wins over everything else (incl. the
  // Shopify checkout gate below and the country gate) because it's the manual
  // operational override. Admin route is unaffected (App routes admin paths
  // before AppContent ever mounts).
  if (config.runtime_controls?.maintenance_enabled) {
    return <MaintenanceScreen />;
  }

  // Shopify checkout caído (breaker abierto y SIN Modo TEST → el backend manda
  // checkout_available=false): el funnel no debe avanzar si no se puede
  // completar el pedido. Reusa la pantalla suave de "actualizando carta" con
  // reintento. Va POR DEBAJO del mantenimiento de ops a propósito.
  if (funnelMode !== 'kcal' && config.checkout_available === false && currentStep > 1) {
    return <ConfigUnavailableScreen onRetry={() => refreshConfig()} retrying={isLoading} />;
  }

  // Gate del checkout PrestaShop: con el flag OFF (default mientras el
  // mapping de productos no esté listo), patitasco.com ve una pantalla
  // operacional temprana en vez de un funnel que falla en el último paso.
  if (
    funnelMode !== 'kcal' &&
    platform === 'prestashop' &&
    config.runtime_controls?.prestashop_checkout_enabled !== true
  ) {
    return <PrestaShopGateScreen />;
  }

  // Show country gate only when geo detection has completed AND country is not
  // supported. If detection is still pending or failed, show the funnel.
  // En modo kcal la calculadora es informativa (no vende ni envía), así que se
  // sirve en cualquier país — igual que los gates de checkout/PrestaShop de
  // arriba, que también se omiten en kcal.
  if (funnelMode !== 'kcal' && geoResult !== null && geoResult.is_supported === false) {
    return (
      <>
        <CountryGatePage
          detectedCountry={geoResult.country}
          supportedCountries={geoResult.supported_countries}
          onCountryOverride={handleCountryOverride}
        />
        <GeoDebugBadge geoResult={geoResult} />
      </>
    );
  }

  if (updateMode.isUpdateMode && updateHydrating) {
    return <UpdateModeLoader name={formData.nombre || 'tu perro'} />;
  }

  if (updateMode.isUpdateMode && updateHydrationError) {
    return <UpdateModeError message={updateHydrationError} />;
  }

  function renderStep() {
    if (currentStep >= 2 && formData.animalType === AnimalType.GATO) {
      return <StepGatoComingSoon />;
    }
    const Pantalla = order[currentStep];
    return Pantalla ? <Pantalla /> : null;
  }

  const isModalVisible = showResumeModal && !modalDismissed && draftSnapshot !== null && !updateMode.isUpdateMode;

  const razaKey = draftSnapshot?.formData?.raza;
  const pesoVal = draftSnapshot?.formData?.peso;
  const modalSummary = {
    nombre: draftSnapshot?.formData?.nombre ?? '',
    raza: razaKey ? config?.breed_database?.breeds?.[razaKey]?.display_name : undefined,
    peso: pesoVal && Number(pesoVal) > 0 ? `${pesoVal} kg` : undefined,
    breedKey: razaKey,
  };
  const daysAgo = draftSnapshot
    ? Math.max(0, Math.floor((Date.now() - draftSnapshot.lastUpdatedAt) / (24 * 60 * 60 * 1000)))
    : 0;

  return (
    <FunnelModeContext.Provider value={funnelMode}>
      {updateMode.isUpdateMode && (
        <div
          role="status"
          aria-live="polite"
          className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-center flex-shrink-0"
          style={{ position: 'sticky', top: 0, zIndex: 50 }}
        >
          <p className="font-poppins text-sm text-amber-800 leading-snug">
            Actualizando plan de <strong>{formData.nombre || 'tu perro'}</strong> — modifica lo que necesites y confirma
            al final
          </p>
        </div>
      )}
      {renderStep()}
      {isModalVisible && (
        <ResumeModal
          summary={modalSummary}
          daysAgo={daysAgo}
          onContinue={() => setModalDismissed(true)}
          onStartFresh={() => {
            setModalDismissed(true);
            resetFunnel();
          }}
        />
      )}
      <GeoDebugBadge geoResult={geoResult} />
    </FunnelModeContext.Provider>
  );
}

function App() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  // TODO /admin/* va al backoffice y NUNCA pasa por los gates del funnel
  // (mantenimiento, país, PrestaShop) — antes solo /admin/configuracion estaba
  // excluido y una subruta admin desconocida caía al funnel gateado
  //.
  if (path.startsWith('/admin')) {
    return (
      <ErrorBoundary>
        <AppConfigProvider>
          <Suspense fallback={<div>Cargando...</div>}>
            <AdminConfiguracion />
          </Suspense>
        </AppConfigProvider>
      </ErrorBoundary>
    );
  }

  // /mi-cuenta — self-service multi-pet del cliente. No necesita FormProvider,
  // pero sí lee config ligera para ciclo de suscripción, soporte y textos.
  if (path.startsWith('/mi-cuenta')) {
    return (
      <ErrorBoundary>
        <AppConfigProvider>
          <Suspense fallback={<div>Cargando...</div>}>
            <MiCuentaPage />
          </Suspense>
        </AppConfigProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AppConfigProvider>
        <FormProvider>
          <AppContent />
          <ChatWidget />
        </FormProvider>
      </AppConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
